import "core-js/stable";
import "regenerator-runtime/runtime";
import { ws } from 'src/api/ws';

const INVALID_TOKEN = 'invalid_token';

let timer_id;
const batch = { total: 0, items: [] };

function createInvocation(query, resolver) {
  console.debug('Excel365: createInvocation');

  batch.total += query.length;
  const promise = new Promise((resolve, reject) => batch.items.push({ query, resolve, reject }));

  const execute = () => {
    batch.items.length && executeInvocations(batch.items, resolver);
    Object.assign(batch, { total: 0, items: [] });
  }

  if (batch.items.length === 1) {
    timer_id = setTimeout(execute, 200);
  }
  else if (batch.total > ws.maximumPayloadSize) {
    clearTimeout(timer_id);
    execute();
  }
  return promise;
}

// https://docs.microsoft.com/en-us/office/dev/add-ins/excel/custom-functions-overview
// https://github.com/OfficeDev/PnP-OfficeAddins

/**
* Run SOQL query.
* @customfunction
* @param {string} query SOQL query
* @return {object} - return result
* @helpurl https://www.xappex.com/knowledge-base/using-in-cell-formulas-to-run-soql-queries-in-xl-connector-365/
*/
async function runquery(query) {
  return await createInvocation(query, (resolve, result) => resolve(result[0].length === 1 ? result[0][0] : result[0].join(', ')));
}

/**
* Returns aggregated financial data from the Accounting Seed Financial Cube object.
* @customfunction
* @param {string} ledger Ledger type, e.g. “Budget”
* @param {string} glAccount GL Account Name, e.g. “5060-Freight”
* @param {string} glav1 GL Variable 1 Name, “ALL” - to calculate the formula based on records associated with any variable1 or no variable1 at all, or “NONE” - to calculate the formula based on records not associated with any variable1.
* @param {string} glav2 GL Variable 2 Name, “ALL” - to calculate the formula based on records associated with any variable2 or no variable2 at all, or “NONE” - to calculate the formula based on records not associated with any variable1.
* @param {string} glav3 GL Variable 3 Name, “ALL” - to calculate the formula based on records associated with any variable3 or no variable3 at all, or “NONE” - to calculate the formula based on records not associated with any variable1.
* @param {string} glav4 GL Variable 4 Name, “ALL” - to calculate the formula based on records associated with any variable4 or no variable4 at all, or “NONE” - to calculate the formula based on records not associated with any variable1.
* @param {string} period Accounting period, e.g. “2019-05”
* @param {string} type Amount type. Possible values are: “OPB” (Opening Balance), “BUD” (Budget Amount), “MTD” (Month-to-date actual), “YTD” (Year-to-date actual)
* @return {number} - return number
* @helpurl https://www.xappex.com/knowledge-base/integration-with-accounting-seed-2/
*/
async function asfc(ledger, glAccount, glav1, glav2, glav3, glav4, period, type) {
  const params = { ledger, glAccount, glav1, glav2, glav3, glav4, period, type };
  Object.keys(params).reduce((out, key) => (out[key] = (out[key] || '').trim().replace(/(['"])/g, '\\$1'), out), params);

  if (!params.ledger) { throw new Error('ERROR: Ledger is required') }
  if (!params.glAccount) { throw new Error('ERROR: GL Account is required') }
  if (!params.period) { throw new Error('ERROR: Period is required') }
  if (!params.type) { throw new Error('ERROR: Amount Type is required') }

  const types = {
    OPB: 'sum(AcctSeed__Opening_Balance__c)',
    BUD: 'sum(AcctSeed__Amount__c)',
    MTD: 'sum(AcctSeed__Current_Period__c)',
    YTD: 'sum(AcctSeed__Year_To_Date__c)',
  };

  const objectType = types[type.toUpperCase()];
  if (!objectType) { throw new Error('ERROR: Invalid parameter: "Type"') }

  const gravX = (x) => {
    const name = params[`glav${x}`];
    const key = name.toUpperCase();

    if (key === 'ALL') { return '' }
    if (!key || key === 'NONE') { return `AND AcctSeed__GL_Account_Variable_${x}__c = NULL` }
    return `AND AcctSeed__GL_Account_Variable_${x}__r.Name = '${name}'`;
  }

  const query = `SELECT ${objectType} FROM AcctSeed__Financial_Cube__c WHERE AcctSeed__Ledger__r.Name = '${params.ledger}' AND AcctSeed__GL_Account__r.Name = '${params.glAccount}' AND AcctSeed__Accounting_Period__r.Name = '${params.period}' ${gravX(1)} ${gravX(2)} ${gravX(3)} ${gravX(4)}`;

  return await createInvocation(query, (resolve, result) => resolve(result[0][0] || 0));
}

async function executeInvocations(invocations, resolver) {
  try {
    let current = 0;
    await ws.action('runquery', { queries: invocations.map(({ query }) => query) }, {
      callback: (results) => {
        results.forEach(({ result, error }) => {
          const { resolve, reject } = invocations[current++];

          if (error) { return reject(new CustomFunctions.Error(CustomFunctions.ErrorCode.InvalidValue, error)) }
          if (!Array.isArray(result) || !result.length) { return resolve() }

          resolver(resolve, result)
        });
      }
    });
  }
  catch (error) {
    const message = (error.status === 401 || error === INVALID_TOKEN) ? 'ERROR: Please log in to Salesforce using XL-Connector 365 plugin.' : error?.message;
    const result = new CustomFunctions.Error(CustomFunctions.ErrorCode.InvalidValue, message);
    invocations.forEach(({ reject }) => reject(result));
  }
}

CustomFunctions.associate("RUNQUERY", runquery);
CustomFunctions.associate("ASFC", asfc);